import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { getBlogPostBySlug, BlogPost } from '../services/api';
import { ROUTES } from '../constants/routes';

export const BlogPostDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchPost = async () => {
            if (!slug) return;
            try {
                const data = await getBlogPostBySlug(slug);
                setPost(data);
            } catch (err) {
                console.error("Failed to fetch blog post:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    if (loading) {
        return (
            <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
                <PublicHeader />
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
                </div>
                <PublicFooter />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
                <PublicHeader />
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                    <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">error_outline</span>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Artículo no encontrado</h1>
                    <Link to={ROUTES.BLOG} className="text-brand hover:underline">Volver al Blog</Link>
                </div>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-200 font-body">
            <PublicHeader />

            <div className="flex-1 w-full max-w-[900px] mx-auto px-4 sm:px-8 pt-28 pb-20">
                <Link to={ROUTES.BLOG} className="inline-flex items-center gap-2 text-gray-500 hover:text-brand mb-8 transition-colors">
                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                    Volver al Blog
                </Link>

                <header className="mb-10 text-center">
                    <div className="flex items-center justify-center gap-3 text-sm mb-4">
                        <span className="px-3 py-1 bg-brand/10 text-brand rounded-full font-bold uppercase text-xs tracking-wider border border-brand/20">
                            {post.category}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500 dark:text-gray-400">{post.read_time} min de lectura</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-display text-gray-900 dark:text-white mb-6 leading-tight">
                        {post.title}
                    </h1>
                    <div className="flex items-center justify-center gap-3">
                        <div className="size-10 rounded-full bg-gray-700 flex items-center justify-center text-sm text-white font-bold">
                            {post.author.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-left">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{post.author}</p>
                            <p className="text-xs text-gray-500">{new Date(post.published_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                </header>

                {post.image && (
                    <div className="rounded-2xl overflow-hidden shadow-lg mb-12 border border-gray-200 dark:border-white/10">
                        <img src={post.image} alt={post.title} className="w-full h-auto object-cover max-h-[500px]" />
                    </div>
                )}

                <article className="prose prose-lg dark:prose-invert max-w-none prose-img:rounded-xl prose-headings:font-display prose-headings:font-bold prose-a:text-brand hover:prose-a:underline">
                    {/* Ideally we would use react-markdown here, but for now rendering raw text properly with whitespace handled */}
                    <div className="whitespace-pre-wrap">{post.content}</div>
                </article>

                <hr className="my-12 border-gray-200 dark:border-white/10" />

                <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-8 text-center">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">¿Te gustó este artículo?</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">Suscríbete a nuestro newsletter para recibir las últimas novedades en ciberseguridad.</p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input type="email" placeholder="tu@email.com" className="flex-1 bg-white dark:bg-black/20 border border-gray-300 dark:border-white/10 rounded-lg px-4 py-2 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand focus:border-transparent outline-none" />
                        <button className="bg-brand text-white font-bold py-2 px-6 rounded-lg hover:bg-brand-dark transition-colors">
                            Suscribirse
                        </button>
                    </div>
                </div>
            </div>

            <PublicFooter />
        </div>
    );
};
